import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ShoppingBag, Star, Truck, Store, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ShareProduct from "../components/ShareProduct";
import { toast } from "sonner";

export default function ProductDetail() {
  const { pid } = useParams();
  const { add } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, average: 0 });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(false);

  const loadReviews = async () => {
    try {
      const { data } = await api.get(`/shop/products/${pid}/reviews`);
      setReviews(data);
    } catch {}
  };

  useEffect(() => {
    let mounted = true;
    api.get(`/shop/products/${pid}`).then((r) => { if (mounted) setProduct(r.data); }).catch(() => setErr(true));
    loadReviews();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [pid]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to write a review");
    setSubmitting(true);
    try {
      await api.post(`/shop/products/${pid}/reviews`, { rating, comment });
      setComment("");
      setRating(5);
      toast.success("Review posted");
      await loadReviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post review");
    } finally {
      setSubmitting(false);
    }
  };

  if (err) return <div data-testid="product-not-found" className="pt-32 text-center text-white/70">Product not found. <Link to="/shop" className="text-tv-accent">Back to shop →</Link></div>;
  if (!product) return <div className="pt-32 min-h-screen" />;

  const images = product.images && product.images.length ? product.images : ["https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800&q=80"];

  return (
    <div data-testid="product-detail-page" className="pt-32 pb-20 px-4 sm:px-8 md:px-12 max-w-6xl mx-auto">
      <Link to="/shop" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Gallery */}
        <div data-testid="product-gallery">
          <div className="aspect-square bg-black rounded-2xl overflow-hidden">
            <img data-testid="product-main-image" src={images[activeImg]} alt={product.title} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${i === activeImg ? "border-tv-accent" : "border-white/10"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {product.creator_name && (
            <Link to={`/creator/${product.creator_id}`} data-testid="product-creator-link" className="text-xs uppercase tracking-widest text-tv-accent font-bold hover:underline inline-flex items-center gap-1">
              <Store className="w-3 h-3" /> {product.creator_name}
            </Link>
          )}
          <h1 data-testid="product-title" className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight mt-2">{product.title}</h1>

          <div className="flex items-center gap-3 mt-3">
            <div data-testid="product-rating" className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`w-4 h-4 ${s <= Math.round(reviews.average) ? "fill-tv-accent text-tv-accent" : "text-white/20"}`} />
              ))}
              <span className="text-white/70 text-sm ml-1">{reviews.average ? reviews.average.toFixed(1) : "—"} ({reviews.count})</span>
            </div>
            {product.is_dropship && (
              <span data-testid="product-dropship-badge" className="text-[10px] uppercase font-bold bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded flex items-center gap-1">
                <Truck className="w-3 h-3" /> Dropship · {product.dropship_vendor || "supplier"}
              </span>
            )}
          </div>

          <div data-testid="product-price" className="text-tv-accent font-display font-black text-4xl mt-5">${product.price_usd.toFixed(2)}</div>
          {product.description && <p data-testid="product-description" className="text-white/80 mt-4 whitespace-pre-line">{product.description}</p>}

          <div className="text-white/60 text-xs mt-3">
            {product.stock > 0 ? <span data-testid="product-stock" className="text-green-300">In stock · {product.stock} available</span> : <span className="text-red-300">Out of stock</span>}
            {product.category && <span className="ml-3">Category: <span className="text-white/80">{product.category}</span></span>}
          </div>

          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center bg-white/5 border border-white/10 rounded-full">
              <button data-testid="product-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 text-white">-</button>
              <span data-testid="product-qty" className="w-10 text-center text-white font-bold">{qty}</span>
              <button data-testid="product-qty-inc" onClick={() => setQty(qty + 1)} className="w-9 h-9 text-white">+</button>
            </div>
            <button
              data-testid="product-add-to-cart"
              onClick={() => add(product.id, qty)}
              disabled={product.stock <= 0}
              className="flex-1 bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold py-3 rounded-full flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" /> Add to cart
            </button>
          </div>

          <div className="mt-3">
            <ShareProduct product={product} />
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section data-testid="reviews-section" className="mt-14">
        <h2 className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight mb-4">Reviews</h2>

        <form onSubmit={submitReview} data-testid="review-form" className="tv-glass rounded-2xl p-5 mb-6">
          <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-2">Your rating</div>
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`review-star-${s}`}
                onClick={() => setRating(s)}
                className="transition-transform hover:scale-110"
              >
                <Star className={`w-7 h-7 ${s <= rating ? "fill-tv-accent text-tv-accent" : "text-white/25"}`} />
              </button>
            ))}
          </div>
          <textarea
            data-testid="review-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={user ? "Share your experience…" : "Sign in to write a review"}
            rows={3}
            maxLength={500}
            disabled={!user}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none disabled:opacity-50"
          />
          <button
            type="submit"
            data-testid="review-submit-btn"
            disabled={submitting || !user}
            className="mt-3 bg-tv-accent hover:bg-yellow-400 text-black font-bold px-5 py-2 rounded-full disabled:opacity-50 text-sm"
          >
            {submitting ? "Posting…" : "Post review"}
          </button>
        </form>

        {reviews.reviews.length === 0 ? (
          <div data-testid="reviews-empty" className="text-white/50 text-sm">No reviews yet. Be the first!</div>
        ) : (
          <div className="space-y-3" data-testid="reviews-list">
            {reviews.reviews.map((r) => (
              <div key={r.id} data-testid={`review-row-${r.id}`} className="tv-glass rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-white">{r.user_name}</div>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-tv-accent text-tv-accent" : "text-white/20"}`} />
                    ))}
                  </div>
                </div>
                {r.comment && <p className="text-white/80 mt-2 text-sm whitespace-pre-line">{r.comment}</p>}
                <div className="text-white/40 text-[11px] mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
